import { yupResolver } from '@hookform/resolvers/yup';
// Removed import from @shared/documents
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useAuth } from '../../hooks/useAuth';
import useFetch from '../../hooks/useFetch';
import useSimpleMutation from '../../hooks/useSimpleMutation';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	LoadingMessage,
} from '../Shared/CommonStyles';
import {
	Button,
	FormContainer,
	ErrorMessage as FormErrorMessage,
	FormGroup,
	Input,
	Label,
} from '../Shared/FormStyles';

// Define the type locally
interface AvisDocument {
	_id: string;
	content: string;
}

type AvisFormData = Omit<AvisDocument, '_id'>;

const avisSchema = yup.object().shape({
	content: yup
		.string()
		.required('Aquest camp és obligatori')
		.min(1, 'El contingut és massa curt')
		.max(200, 'El contingut no pot tenir més de 200 caràcters'),
});

const AvisForm = ({ mode }: { mode: string }) => {
	const queryClient = useQueryClient();
	const { id } = useParams();
	const { user } = useAuth();

	const [authError, setAuthError] = useState<string | null>(null);

	const isAuthenticated =
		!!user &&
		(user.roles.includes('Admin') || user.roles.includes('Agrupament'));
	const isEditMode = mode === 'edit';

	const {
		data: avis,
		isLoading: isFetchingAvis,
		error: fetchAvisError,
	} = useFetch<AvisDocument>({
		queryKey: ['avis', id],
		url: `/api/avisos/${id}`,
	});

	const putMutation = useSimpleMutation<AvisFormData>({
		method: 'put',
		url: `/api/avisos/${id}`,
		dependencies: [id],
		navigateUrl: '/inici',
	});

	const postMutation = useSimpleMutation<AvisFormData>({
		method: 'post',
		url: '/api/avisos',
		dependencies: [],
		navigateUrl: '/inici',
	});

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({ resolver: yupResolver(avisSchema) });

	const invalidateQueries = () => {
		if (isEditMode) {
			queryClient.invalidateQueries({ queryKey: ['avis', id] });
		}
		queryClient.invalidateQueries({ queryKey: ['avisos'] });
	};

	const onSubmit = async (data: AvisFormData) => {
		if (isEditMode) {
			putMutation.mutate(data, {
				onSuccess: () => {
					invalidateQueries();
				},
			});
		} else {
			postMutation.mutate(data, {
				onSuccess: () => {
					invalidateQueries();
				},
			});
		}
	};

	useEffect(() => {
		if (!user) {
			setAuthError('No estàs autenticat');
		} else if (!isAuthenticated) {
			setAuthError('No tens permís');
		} else {
			setAuthError(null);
		}
	}, [isAuthenticated]);

	useEffect(() => {
		if (avis) {
			reset(avis);
		}
	}, [reset, avis]);

	const loading =
		isFetchingAvis || putMutation.isPending || postMutation.isPending;

	const error =
		authError || fetchAvisError || putMutation.error || postMutation.error;

	return loading ? (
		<LoadingMessage>
			<LoadingGif size="large" />
		</LoadingMessage>
	) : error ? (
		<ErrorMessage>{extractErrorMessage(error)}</ErrorMessage>
	) : (
		<FormContainer onSubmit={handleSubmit(onSubmit)}>
			{/* <h2>{isEditMode ? 'Editar Avís' : 'Crear Avís'}</h2> */}

			<FormGroup>
				<Label htmlFor="content">Contingut:</Label>
				<Input id="content" {...register('content')} />
				{errors.content && (
					<FormErrorMessage>
						{errors.content.message?.toString()}
					</FormErrorMessage>
				)}
			</FormGroup>

			<Button type="submit" disabled={loading}>
				{loading
					? 'Guardant...'
					: isEditMode
						? 'Desar Canvis'
						: 'Crear Avís'}
			</Button>
		</FormContainer>
	);
};

export default AvisForm;
